import { APPT_LOCATION_TYPES, EVENT_STATUS } from "constant/AppConstants";
import { formatDateAndTime } from "constant/calenderView";
import {
  CUSTOMER_CARE_HOURS,
  CUSTOMER_CARE_OPTION,
  CUSTOMER_CARE_PHONE,
  CUSTOMER_CARE_PHONE_HREF,
} from "constant/patientPortalConstants";
import { Appointment } from "types/appointments";
import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { myContext } from "hooks/useContext/useContext";
import { Cancel_Name } from "../forms/clientAppointmentForms/CancelAppointments/staticCancelData";
import { useAuth0 } from "react-auth0-spa";
import { ApiCalls } from "../forms/providerAppointmentForms/AllAppointmentsApi";
import { clientRescheduleAndCancelCall } from "../forms/providerAppointmentForms/AllAppointmentsFunction";
import {
  isClientAppointmentCancelled,
  isClientAppointmentOver,
  isClientAppointmentSyncFailed,
  isClientAppointmentSyncing,
  parseVideoLink,
  VideoLink,
} from "./clientAppointmentStatus";

interface IProps {
  appointment: Appointment;
  patientClinic?: number[];
  activeDay: string;
  path: string;
  displayBanner: Boolean;
  pollCount?: number;
  isUpNext?: boolean;
  onContactUs?: () => void;
  // Current time (ms) from the day list's clock, so "over" updates on its own.
  now?: number;
}

type CancelDialog = "notCancellable" | "checkFailed";

const GOOGLE_MAPS_URL = "https://www.google.com/maps?q=";
// The family's own cancel stays "in progress" for the first couple of polls.
const CANCELLING_POLL_WINDOW = 2;

const formatTime = (iso: string) => formatDateAndTime(iso).toLocaleLowerCase();

const ClientAppointmentBar: React.FC<IProps> = ({
  appointment,
  patientClinic,
  activeDay,
  path,
  displayBanner,
  pollCount,
  isUpNext = false,
  onContactUs,
  now,
}) => {
  const { setValidateUrl, cancellingAppt, setCancellingAppt } =
    useContext(myContext);
  const { getTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [checking, setChecking] = useState<boolean>(false);
  // Kind is kept after closing so the text does not change while the dialog fades out.
  const [dialog, setDialog] = useState<CancelDialog>("notCancellable");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const openDialog = (kind: CancelDialog) => {
    setDialog(kind);
    setDialogOpen(true);
  };

  const isTelehealth =
    appointment.location_type === APPT_LOCATION_TYPES.telehealth;
  const address = appointment.address;
  const apptAddress = [
    address?.addressLine1,
    address?.addressLine2,
    address?.city,
    address?.state,
    address?.zipCode,
  ]
    .filter((part) => part !== undefined && part !== "" && part !== null)
    .join(", ");

  const handleCancelnavigation = () => {
    setValidateUrl(false);
    navigate("/client-appointment-redirect", {
      state: {
        appointment: appointment,
        activeDay: activeDay,
        path: path,
        flow: Cancel_Name,
      },
    });
  };

  // Before opening the cancel flow, ask Axon whether the visit has already been
  // converted; a converted visit can't be cancelled from the patient portal.
  const handleCancelClick = async () => {
    setChecking(true);
    try {
      const response = await clientRescheduleAndCancelCall(
        ApiCalls.PatientConfirmConversionCall,
        { eventId: appointment?.id, timezone: appointment?.clinic?.timezone },
        getTokenSilently
      );
      if (response?.data?.isConverted) {
        openDialog("notCancellable");
      } else {
        handleCancelnavigation();
      }
    } catch (error) {
      openDialog("checkFailed");
    } finally {
      setChecking(false);
    }
  };

  const isSyncing = isClientAppointmentSyncing(appointment);
  const isFailed = isClientAppointmentSyncFailed(appointment);
  const isCancelled = isClientAppointmentCancelled(appointment);
  const isOver = isClientAppointmentOver(
    appointment,
    now === undefined ? moment() : moment(now)
  );

  useEffect(() => {
    if (cancellingAppt && pollCount && pollCount > CANCELLING_POLL_WINDOW) {
      setCancellingAppt(null);
    }
  }, [pollCount, cancellingAppt, setCancellingAppt]);

  const cancellingAppointment =
    appointment.id === cancellingAppt &&
    pollCount !== undefined &&
    pollCount < CANCELLING_POLL_WINDOW;

  const isEligibleForCancel =
    patientClinic?.includes(appointment?.home_clinic_id) &&
    !isSyncing &&
    !appointment?.scheduledStatus?.includes(EVENT_STATUS.canceled) &&
    !isCancelled &&
    !cancellingAppointment &&
    !isOver;
  const showCancel = isEligibleForCancel && !isFailed && !displayBanner;
  // Saved details that aren't a link (e.g. a meeting ID and passcode) are shown
  // as plain text so they're never lost.
  const { joinUrl, details: videoDetails }: VideoLink = isTelehealth
    ? parseVideoLink(appointment?.link)
    : {};
  const showJoin =
    isUpNext &&
    !!joinUrl &&
    !isCancelled &&
    !cancellingAppointment;
  // A failed sync always shows, even on a cancelled visit: the cancel may not
  // have reached CR/Athena, so the family needs the number to call.
  const showNeedsAttention = isFailed;
  const showUpdating = (isSyncing || cancellingAppointment) && !isFailed;
  const isDimmed = isOver || isCancelled;
  const title = `${appointment.client_name}'s ${appointment?.title}`;

  const barColor = showNeedsAttention
    ? "warning.main"
    : isCancelled
    ? "divider"
    : "primary.main";

  const location = isTelehealth ? (
    <>
      <VideocamOutlinedIcon fontSize="small" />
      <span style={{ overflowWrap: "anywhere" }}>
        {joinUrl &&
        !isOver &&
        !isCancelled &&
        !cancellingAppointment &&
        !showJoin ? (
          <Link href={joinUrl} target="_blank" rel="noopener noreferrer">
            Join video visit
          </Link>
        ) : (
          "Video visit"
        )}
        {videoDetails && !isCancelled ? ` · ${videoDetails}` : ""}
      </span>
    </>
  ) : apptAddress ? (
    <>
      <PlaceOutlinedIcon fontSize="small" />
      <Link
        href={`${GOOGLE_MAPS_URL}${encodeURIComponent(apptAddress)}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ overflowWrap: "anywhere" }}
      >
        {apptAddress}
      </Link>
    </>
  ) : null;

  const cancelButton = showCancel ? (
    <Button
      onClick={handleCancelClick}
      disabled={checking}
      aria-label={`Cancel ${title} at ${formatTime(appointment.startAt)}`}
      startIcon={checking ? <CircularProgress size={14} color="inherit" /> : undefined}
      sx={{ minHeight: 44, minWidth: 64 }}
    >
      {checking ? "Checking…" : "Cancel"}
    </Button>
  ) : null;

  return (
    <>
      <Box
        data-testid="client-appointment-row"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "4px 1fr auto", sm: "4px 96px 1fr auto" },
          columnGap: { xs: 1.5, sm: 2 },
          rowGap: 0.5,
          alignItems: "center",
          py: 1.75,
          px: isUpNext ? 1.5 : 0,
          mx: isUpNext ? -1.5 : 0,
          borderRadius: 1,
          bgcolor: isUpNext ? "primary.light" : "transparent",
        }}
      >
        <Box
          sx={{
            alignSelf: "stretch",
            gridRow: { xs: "1 / span 2", sm: "auto" },
            borderRadius: 0.5,
            bgcolor: barColor,
            opacity: isDimmed && !showNeedsAttention ? 0.4 : 1,
          }}
        />
        <Box sx={{ gridColumn: { xs: 2, sm: "auto" } }}>
          <Typography
            variant="body2"
            color={isDimmed ? "text.secondary" : "text.primary"}
            sx={{ fontWeight: 500 }}
          >
            {formatTime(appointment.startAt)}
          </Typography>
          {appointment.endAt && (
            <Typography variant="caption" color="text.secondary">
              to {formatTime(appointment.endAt)}
            </Typography>
          )}
        </Box>
        <Box sx={{ gridColumn: { xs: 2, sm: "auto" }, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            component="div"
            color={isDimmed ? "text.secondary" : "text.primary"}
            sx={{ fontWeight: 500, lineHeight: 1.5 }}
          >
            {isUpNext && (
              <Chip label="Up next" color="primary" size="small" sx={{ mr: 1 }} />
            )}
            {isCancelled ? <del>{title}</del> : title}
            {isCancelled && <Chip label="Cancelled" size="small" sx={{ ml: 1 }} />}
            {showUpdating && (
              <Chip
                label={cancellingAppointment ? "Cancelling" : "Updating"}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ ml: 1, bgcolor: "background.paper" }}
              />
            )}
            {showNeedsAttention && (
              <Chip label="Needs attention" color="warning" size="small" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            columnGap={2}
            rowGap={0.25}
            sx={{
              color: "text.secondary",
              typography: "body2",
              "& > span": { display: "inline-flex", alignItems: "center", gap: 0.75 },
            }}
          >
            <span>
              <PersonOutlineIcon fontSize="small" />
              {appointment?.provider_name?.replace(/,/g, "") ?? ""}
            </span>
            {location && <span>{location}</span>}
          </Stack>
          {showUpdating && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
              {cancellingAppointment
                ? "We're cancelling this appointment. This usually takes a minute, and this page updates by itself."
                : "We're saving a change to this appointment. This usually takes a minute, and this page updates by itself."}
            </Typography>
          )}
          {showNeedsAttention && (
            <Typography variant="caption" component="p" sx={{ mt: 0.5, color: "warning.dark" }}>
              We couldn't update this appointment. Please call{" "}
              <Link href={CUSTOMER_CARE_PHONE_HREF} color="inherit">
                {CUSTOMER_CARE_PHONE}
              </Link>
              , {CUSTOMER_CARE_OPTION}, and we'll sort it out.
            </Typography>
          )}
        </Box>
        {showJoin ? (
          <Stack
            direction={{ xs: "row", sm: "column" }}
            alignItems={{ xs: "center", sm: "flex-end" }}
            spacing={0.5}
            sx={{ gridColumn: { xs: "2 / -1", sm: "auto" } }}
          >
            <Button
              variant="contained"
              href={joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<VideocamOutlinedIcon />}
              sx={{ minHeight: 44, flexGrow: { xs: 1, sm: 0 } }}
            >
              Join video visit
            </Button>
            {cancelButton}
          </Stack>
        ) : (
          <Box sx={{ gridColumn: { xs: 3, sm: "auto" }, gridRow: { xs: "1 / span 2", sm: "auto" } }}>
            {cancelButton}
          </Box>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby={`cancel-dialog-title-${appointment.id}`}
      >
        <DialogTitle id={`cancel-dialog-title-${appointment.id}`}>
          {dialog === "notCancellable"
            ? "This appointment can't be cancelled online"
            : "We couldn't check this appointment"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialog === "notCancellable"
              ? `Our team needs to make this change for you. Please call Customer Care at ${CUSTOMER_CARE_PHONE}, ${CUSTOMER_CARE_OPTION}, from ${CUSTOMER_CARE_HOURS}, or send us a message.`
              : `Please try again in a few minutes. If it keeps happening, call Customer Care at ${CUSTOMER_CARE_PHONE}, ${CUSTOMER_CARE_OPTION}.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ minHeight: 44 }}>
            Close
          </Button>
          {dialog === "notCancellable" && onContactUs && (
            <Button
              variant="contained"
              onClick={() => {
                setDialogOpen(false);
                onContactUs();
              }}
              sx={{ minHeight: 44 }}
            >
              Send a message
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(ClientAppointmentBar);
